import * as fromBusinessLine from '../modules/business-line-selection/reducers/business-line-page.reducer';
import * as fromLocationPage from '../modules/location/reducers/location-page.reducer';
import * as fromRetailReviewPage from '../modules/retail-review/reducers';
import * as fromRetailSpecificationPage from '../modules/retail-specification/reducers';
import * as fromReviewPage from '../modules/review/reducers';
import * as fromRmsSpecificationPage from '../modules/rms-specification/reducers';
import * as fromRmsSummaryPage from '../modules/rms-summary/reducers';
import * as fromSpecificationPage from '../modules/specification/reducers';
import * as fromStepperState from './stepper/stepper.reducer';
import * as fromSummaryPage from '../modules/summary/reducers';
import * as fromBlkSummaryPage from '../modules/multiple-order-summary/reducers';
import * as fromLocationSpecificationPage from '../modules/location-specification/reducers';
import * as fromMultipleOrderConfirmationPage from '../../multiple-order-confirmation/reducers';
import * as fromAdtSpecification from '../modules/adt-specification/store/reducers';
import * as fromAdtSummary from '../modules/adt-summary/store/reducers';

import { ActionReducerMap, combineReducers, createFeatureSelector, createSelector } from '@ngrx/store';
import {
  SpecificationFormState,
  filterUniqueProducts,
  getProducts,
} from '../modules/specification/reducers/specification/specification.reducer';
import { State, getLegalEntity } from '@cemex/cmx-client-layout';

import { BusinessLinesState } from '../modules/business-line-selection/reducers/business-line/business-lines.reducer';
import { IBusinessLineState } from '../modules/business-line-selection/reducers/business-line-page.reducer';
import { ILegalEntity } from '@cemex-core/cmx-api/models/legal-entities/legal-entity';
import { ISalesDocument } from '@cemex-core/cmx-api/models/product/models/sales-document.interface';
import { InjectionToken } from '@angular/core';
import { LocationPageState } from '../modules/location/reducers/location-page.reducer';
import { PaymentTerm, CombinationOP, ValueAddedOP, RmsContractBalance } from '@cemex-core/cmx-api';
import { ShippingConditionsState } from '../modules/business-line-selection/reducers/shipping-condition/shipping-conditions.reducer';
import { isNullOrUndefined } from '@util';

import * as fromTimer from './timer.reducer';
import * as fromAdditionalServices from './../modules/location-specification/reducers/additional-services.reducer';
import * as fromContactPersonRoles from './../modules/location-specification/reducers/contact-person-roles.reducer';
import * as fromReceivingPersons from './../modules/location-specification/reducers/receiving-persons.reducer';
import * as fromContracts from './../modules/location-specification/reducers/contracts.reducer';
import * as fromPlants from './../modules/location-specification/reducers/plants.reducer';
import * as fromUnits from './../modules/location-specification/reducers/unit-reducer/units.reducer';
import * as fromFeatureToggle from './feature-toggle.reducer';

import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { getRepeatOrderDetail } from '@ordersnproducts/modules/shared/modules/repeat-order/selectors/repeat-order.selectors';
export const orderBuilderKey = 'orderBuilder';

export interface OrderBuilderFeatureState {
  stepper: fromStepperState.StepperState;
  businessLinePage: fromBusinessLine.IBusinessLineState;
  locationPage: fromLocationPage.LocationPageState;
  specificationPage: fromSpecificationPage.SpecificationBaseState;
  retailSpecificationPage: fromRetailSpecificationPage.RetailSpecificationState;
  rmsSpecificationPage: fromRmsSpecificationPage.RmsSpecificationState;
  retailReviewPage: fromRetailReviewPage.RetailReviewState;
  reviewPage: fromReviewPage.ReviewBaseState;
  summaryPage: fromSummaryPage.SummaryBaseState;
  rmsSummaryPage: fromRmsSummaryPage.RmsSummaryState;
  timer: fromTimer.TimerState;
  blkSummaryPage: fromBlkSummaryPage.BlkSummaryState;
  locationSpecificationPage: fromLocationSpecificationPage.LocationSpecificationState;
  multipleOrderConfirmationPage: fromMultipleOrderConfirmationPage.MltOrdersConfirmState;
  featureToggle: fromFeatureToggle.FeatureToggleState;
  adtSpecification: fromAdtSpecification.ADTSpecificationState;
  adtSummary: fromAdtSummary.AdtSummaryState;
}

export interface OrderBuilderState extends State {
  orderBuilderState: OrderBuilderFeatureState;
}

export const reducers = {
  stepper: fromStepperState.stepperReducer,
  businessLinePage: fromBusinessLine.reducers,
  locationPage: fromLocationPage.reducers,
  specificationPage: fromSpecificationPage.reducers,
  retailSpecificationPage: fromRetailSpecificationPage.reducers,
  rmsSpecificationPage: fromRmsSpecificationPage.reducers,
  retailReviewPage: fromRetailReviewPage.reducers,
  reviewPage: fromReviewPage.reducers,
  summaryPage: fromSummaryPage.summaryStateReducer,
  rmsSummaryPage: fromRmsSummaryPage.reducers,
  timer: fromTimer.timerReducer,
  blkSummaryPage: fromBlkSummaryPage.reducers,
  locationSpecificationPage: fromLocationSpecificationPage.reducers,
  multipleOrderConfirmationPage: fromMultipleOrderConfirmationPage.reducers,
  featureToggle: fromFeatureToggle.featureToggleReducer,
  adtSpecification: fromAdtSpecification.adtReducers,
  adtSummary: fromAdtSummary.adtSummaryReducers,
};

export const combinedReducers = combineReducers({
  ...reducers,
});

export const reducerToken = new InjectionToken<ActionReducerMap<OrderBuilderState>>('COMBINED_REDUCERS');

export function getReducers() {
  return combinedReducers;
}

export const reducerProvider = { provide: reducerToken, useFactory: getReducers };

export const getOrderBuilderState = createFeatureSelector<OrderBuilderFeatureState>(orderBuilderKey);

export const getStepperState = createSelector(getOrderBuilderState, (state) => {
  return state.stepper;
});

export const getFeatureToggleState = createSelector(getOrderBuilderState, (state) => {
  return state.featureToggle;
});

// Business Line --
export const getBusinessLinePage = createSelector(getOrderBuilderState, (state) => {
  return state.businessLinePage;
});

export const getBusinessLine = createSelector(getBusinessLinePage, (state) => {
  return state.businessLine;
});

export const getBusinessLines = createSelector(getBusinessLinePage, (state) => {
  return state.businessLine.businessLines;
});

export const getBusinessLinesLoading = createSelector(getBusinessLinePage, (state) => {
  return state.businessLine.loading;
});

export const getSelectedBusinessLine = createSelector(getBusinessLinePage, (state) => {
  return state.businessLine.selected;
});

export const getPumpCapacities = createSelector(getBusinessLinePage, (state) => {
  return state.businessLine.capacities && state.businessLine.capacities.pumpCapacities;
});

export const getShippingCondition = createSelector(getBusinessLinePage, (state) => {
  return state.shippingCondition;
});

export const getShippingConditions = createSelector(getBusinessLinePage, (state) => {
  return state.shippingCondition.shippingConditions;
});

export const getSelectedShippingConditions = createSelector(getBusinessLinePage, (state) => {
  return state.shippingCondition.selected;
});

export const getSummaryOrders = createSelector(getOrderBuilderState, (state) => {
  return state.blkSummaryPage.blkOrderState;
});

export const getMultipleOrdersConfirmation = createSelector(getOrderBuilderState, (state) => {
  return state.multipleOrderConfirmationPage.mltOrdersConfirmStateState;
});

export const getLocationSpecificationState = createSelector(getOrderBuilderState, (state) => {
  return state.locationSpecificationPage;
});

export const getLocationSpecificationStateForm = createSelector(getOrderBuilderState, (state) => {
  return state.locationSpecificationPage.locationSpecificationFormState.forms;
});

export const getSelectedBusinessLineAndShippingCondition = createSelector(
  getSelectedBusinessLine,
  getSelectedShippingConditions,
  (businessLine, shippingCondition) => {
    return {
      productLineId: businessLine && businessLine.productTypeId,
      shippingConditionCode: shippingCondition && shippingCondition.shippingConditionCode,
      shippingConditionId: shippingCondition && shippingCondition.shippingConditionId,
    };
  },
);

export const getSelectedBusinessLineAndShippingConditionCodes = createSelector(
  getSelectedBusinessLine,
  getSelectedShippingConditions,
  (businessLine, shippingCondition) => {
    return {
      productLineCode: businessLine && businessLine.productTypeCode,
      shippingConditionCode: shippingCondition && shippingCondition.shippingConditionCode,
    };
  },
);

export const getLegalEntityAndSelectedBusinessLineAndShippingConditionCodes = createSelector(
  getLegalEntity,
  getSelectedBusinessLine,
  getSelectedShippingConditions,
  (customer: ILegalEntity, businessLine, shippingCondition) => {
    return {
      customer,
      productLineCode: businessLine && businessLine.productTypeCode,
      shippingConditionCode: shippingCondition && shippingCondition.shippingConditionCode,
    };
  },
);

export const getTimerSelector = createSelector(getOrderBuilderState, (state) => {
  return state.timer;
});

// Location Page --
export const getLocationPage = createSelector(getOrderBuilderState, (state) => {
  return state.locationPage;
});

export const isVariousLocationJobsite = createSelector(getLocationPage, (state) => {
  if (state.jobsite && state.jobsite.selected) {
    return state.jobsite.selected.isVariousLocation;
  }
  return false;
});

export const getExperiencesState = createSelector(getLocationPage, (state) => state.experiences);
export const getInternalComments = createSelector(getLocationPage, (state) => state.internalComments);

export const getActiveExperienceCode = createSelector(getExperiencesState, (state) => state.activeExperienceCode);
export const getActiveCountryCode = createSelector(getLegalEntity, (state) => state.countryCode.trim());
export const getActiveExperienceCodeAndCountryCode = createSelector(
  getActiveExperienceCode,
  getActiveCountryCode,
  (experienceCode, countryCode) => {
    return {
      experienceCode,
      countryCode,
    };
  },
);
export const getActiveFeatures = createSelector(getExperiencesState, (state) => state.features);

export const getJobsiteState = createSelector(getLocationPage, (state) => {
  return state.jobsite;
});

export const getJobsiteSalesArea = createSelector(getJobsiteState, (state) => {
  return state.jobsiteSalesArea;
});

export const getJobsitesAvailable = createSelector(getJobsiteState, (state) => {
  return state.jobsites;
});

export const getJobsiteSelected = createSelector(getJobsiteState, (state) => {
  return state.selected;
});

export const getHaulerState = createSelector(getLocationPage, (state) => {
  return state.hauler;
});

export const getPointOfDeliveryState = createSelector(getLocationPage, (state) => {
  return state.pointOfDelivery;
});

export const getSelectedPointOfDelivery = createSelector(getPointOfDeliveryState, (state) => state.selected);

export const getPointOfDeliveries = createSelector(getPointOfDeliveryState, (state) => {
  return state.pointOfDeliveries;
});

export const getPointOfDeliverySelected = createSelector(getPointOfDeliveryState, (state) => {
  return state.selected;
});

export const getPointOfDeliveryAddress = createSelector(getPointOfDeliveryState, (state) => {
  return state.address;
});

export const getPointOfDeliveryLoaded = createSelector(getLocationPage, (state) => {
  return state.pointOfDelivery.loaded;
});

export const createdPointOfDeliveryState = createSelector(getPointOfDeliveryState, (state) => {
  return state.createdPointOfDelivery;
});

export const errorPointOfDeliveryState = createSelector(getPointOfDeliveryState, (state) => {
  return state.errorPointOfDelivery;
});

export const getNewPointOfDeliveryCreate = createSelector(
  createdPointOfDeliveryState,
  errorPointOfDeliveryState,
  (createdPointOfDelivery, errorPointOfDelivery) => {
    return {
      createdPointOfDelivery,
      errorPointOfDelivery,
    };
  },
);

export const getAnticipatedInvoiceState = createSelector(getLocationPage, (state) => {
  return state.anticipatedInvoice;
});

export const getNewReceivingPersonState = createSelector(getLocationPage, (state) => {
  return state.newReceivingPerson;
});

export const getRoles = createSelector(getNewReceivingPersonState, (state) => {
  return state.roles;
});

export const getRolesLoader = createSelector(getNewReceivingPersonState, (state) => {
  return state.loadingRoles;
});

export const getCountries = createSelector(getNewReceivingPersonState, (state) => {
  return state.countries;
});

export const getCountriesLoader = createSelector(getNewReceivingPersonState, (state) => {
  return state.loadingCountries;
});

export const getPurchaseOrderFullState = createSelector(getLocationPage, (state) => {
  return state.purchaseOrder;
});

export const getPurchaseOrderState = createSelector(getLocationPage, (state) => ({
  selected: state.purchaseOrder.selected,
  required: state.purchaseOrder.required,
  disabled: state.purchaseOrder.disabled,
  visible: state.purchaseOrder.visible,
  validators: state.purchaseOrder.validators,
}));

export const getPurchaseOrderValidationData = createSelector(
  getSelectedBusinessLine,
  getJobsiteSelected,
  (businessLine, jobsite) => ({
    productLine: businessLine.productTypeCode,
    jobsiteId: jobsite.shipmentLocationId,
  }),
);

export const getPurchaseOrderValidationState = createSelector(getLocationPage, (state) => {
  return state.purchaseOrder.validating;
});

export const getHasPurchaseOrderValidationEnded = createSelector(
  getLocationPage,
  (state) => state && state.purchaseOrder.validationEnded,
);

export const getPurchaseOrderFormControlAsyncErrors = createSelector(
  getLocationPage,
  (state) => state && state.purchaseOrder.errors,
);

export const getPurchaseOrderValidMasks = createSelector(getLocationPage, (state) => state.purchaseOrder.validMasks);

export const getSpecialInstructionsState = createSelector(getLocationPage, (state) => {
  return state.specialInstructions;
});

export const getLocationMapState = createSelector(getLocationPage, (state) => {
  return state.locationMap;
});

export const getJobsiteSalesAreas = createSelector(getJobsiteState, (state) => {
  return state.jobsiteSalesArea;
});

export const getJobsiteDefaultPlants = createSelector(getJobsiteState, (state) => {
  return state.plant;
});

export const getPointOfDeliverySalesAreas = createSelector(getPointOfDeliveryState, (state) => {
  return state.pointOfDeliverySalesArea;
});

export const getStdLotState = createSelector(getJobsiteState, (state) => {
  return state.stdLot;
});

export const getJobsiteFullSalesAreas = createSelector(getJobsiteState, (state) => {
  return state.jobsiteFullSalesArea;
});

export const getCalendarsState = createSelector(getLocationPage, (state) => {
  return state.calendars;
});

export const getCalendars = createSelector(getLocationPage, (state) => {
  return state.calendars.calendars;
});

export const getCalendarsAndJobsiteFullSalesAreas = createSelector(
  getCalendars,
  getJobsiteFullSalesAreas,
  getPointOfDeliverySelected,
  (calendars, jobsiteFullSalesAreas, podSelected) => {
    return {
      calendars,
      jobsiteFullSalesAreas,
      podSelected,
    };
  },
);

export const getFirstCalendar = createSelector(getLocationPage, (state) => {
  return state.calendars?.calendars?.[0];
});

export const getFirstCalendarRestrictedJobsite = createSelector(getFirstCalendar, (state) => {
  return _safe(() => state.jobSite.restricted, false);
});

export const getTemperatureControlBusinessDaysCalendar = createSelector(getFirstCalendar, (state) => {
  return _safe(() => state.temperatureControlBusinessDays, undefined);
});

export const getTemperatureControlBusinessDaysCalendarFiltered = createSelector(
  getTemperatureControlBusinessDaysCalendar,
  (state) => {
    return _safe(() => state.filter((day) => day.type.toUpperCase() === 'OK'), undefined);
  },
);

export const getJobsiteAddress = createSelector(getLocationPage, (state) => {
  return state.jobsite.address;
});

export const getRealJobsiteAddress = createSelector(getLocationPage, (state) => {
  return state.jobsite.realJobsiteAddress;
});

export const getJobsiteAddresAndCalendarType = createSelector(
  getJobsiteAddress,
  getFirstCalendar,
  getSelectedBusinessLine,
  (jobsiteAddress, calendar, businessLineSelected) => {
    return {
      jobsiteAddress,
      calendar,
      businessLineSelected,
    };
  },
);

export const getJobsiteAddresCalendarTypeAndExperience = createSelector(
  getJobsiteAddress,
  getFirstCalendar,
  getSelectedBusinessLine,
  getActiveExperienceCode,
  (jobsiteAddress, calendar, businessLineSelected, experienceCode) => {
    return {
      jobsiteAddress,
      calendar,
      businessLineSelected,
      experienceCode,
    };
  },
);

export const getJobsiteFullSalesAreasExperience = createSelector(
  getJobsiteState,
  getActiveExperienceCode,
  (state, experience) => {
    return { state: state.jobsiteFullSalesArea, experience };
  },
);

// -- Location Page

/**
 * Returns the slice of store state that deals with the specification step of the order process
 * @type {MemoizedSelector<object, SpecificationBaseState>}
 */
export const getSpecificationPage = createSelector(getOrderBuilderState, (state) => {
  return state.specificationPage;
});

export const getRmsSpecificationPage = createSelector(getOrderBuilderState, (state) => {
  return state.rmsSpecificationPage;
});

/**
 * Returns the product-units value of the state of the specification-step
 * @type {MemoizedSelector<object, State>}
 */
export const getSpecificationProductUnits = createSelector(getSpecificationPage, (state) => state.productUnits);

export const getSpecificationPaymentTerm = createSelector(getSpecificationPage, (state) => state.paymentTerm);

export const getSpecificationProductPlants = createSelector(getSpecificationPage, (state) => state.productPlants);

export const getSpecificationOwnfreightPlants = createSelector(getSpecificationPage, (state) => state.ownfreightPlants);

export const getQuantityValue = createSelector(getSpecificationPage, (state) => state.quantity);

export const getDefaultPaymentTerm = createSelector(getSpecificationPaymentTerm, (state) => state.defaultPaymentTerm);

export const getDeliveryTimeWindows = createSelector(getSpecificationPage, (state) => state.deliveryTimeWindows);

export const getCemexPumpingServiceInfo = createSelector(getSpecificationPage, (state) => state.cemexPumpingService);

export const getVehicles = createSelector(getSpecificationPage, (state) => state.vehicles);

export const getSpecificationForm = createSelector(getSpecificationPage, (state) => state.specificationForm);

export const getSpecificationFormProductsRaw = createSelector(getSpecificationForm, (state) => state.productsRaw);

export const getProductSelectionProducts = createSelector(
  getSpecificationForm,
  (state) => state.productSelectionProducts,
);

export const getNextWorkingDay = createSelector(getSpecificationPage, (state) => state.nextWorkingDay.nextWorkingDay);

export const getHolidays = createSelector(getSpecificationPage, (state) => state.disabledDates.holidays);

export const getSpecificationProductCatalog = createSelector(getSpecificationPage, (state) => {
  return state.productCatalog;
});

export const getCatalogsSelector = createSelector(
  getSpecificationForm,
  (state: SpecificationFormState) => state.catalogs,
);

export const getSelectedContractByItemIdFactory = (itemId) => {
  return createSelector(getSpecificationPage, (state) => {
    return Object.keys(state.contracts.availableContracts)
      .reduce((acc, key) => {
        return [...acc, ...state.contracts.availableContracts[key]];
      }, [])
      .find((contract: ISalesDocument) => {
        return contract.salesDocumentItemId === itemId;
      });
  });
};

export const getLoadedProductsAndDefaultPaymentTerms = createSelector(
  getSpecificationForm,
  getDefaultPaymentTerm,
  (state: SpecificationFormState, defaultPaymentTerms: PaymentTerm) => {
    return { loadedProducts: state.productSelectionProducts, defaultPaymentTerm: defaultPaymentTerms };
  },
);

export const getSpecsProductsAll = createSelector(
  getSpecificationForm,
  ({ productsRaw, productSelectionProducts, topProducts }) => {
    return {
      productsRaw,
      productSelectionProducts,
      topProducts,
    };
  },
);

export const getMasterContractState = createSelector(getSpecificationPage, (state) => {
  return state.masterContract;
});

export const getMasterContractSelected = createSelector(getMasterContractState, ({ selectedContract }) => {
  return selectedContract;
});

export const getUsesMasterContract = createSelector(getSpecificationForm, ({ usesMasterContract }) => {
  return usesMasterContract;
});

export const getNewOrderCreation = createSelector(getSpecificationForm, ({ newOrderCreation }) => {
  return newOrderCreation;
});

export const getProductsBySelectedContract = createSelector(
  getSpecsProductsAll,
  getUsesMasterContract,
  getMasterContractSelected,
  getRepeatOrderDetail,
  (
    { productsRaw, productSelectionProducts, topProducts },
    usesMasterContract: boolean,
    masterContract: ISalesDocument,
    orderDetail,
  ) => {
    if (!usesMasterContract) {
      if (orderDetail) {
        const topList = (prod1) =>
          prod1.commercialDesc ===
          (_safe(() => orderDetail.items[0].product.commercialDesc) ||
            _safe(() => orderDetail.orderProducts[0]['commercialDesc']));
        const selectList = (prod2) =>
          prod2.commercialDesc ===
          (_safe(() => orderDetail.items[0].product.commercialDesc) ||
            _safe(() => orderDetail.orderProducts[0]['commercialDesc']));
        if (topProducts.some(topList) && productSelectionProducts.some(selectList)) {
          return productSelectionProducts;
        } else {
          return topProducts;
        }
      } else {
        return productSelectionProducts;
      }
    }

    // We need even duplicated products to match masterContractId (salesDocumentId)
    // First we match all products with master contract and then remove the duplicates
    return filterUniqueProducts(
      productsRaw.filter((product) => {
        if (
          isNullOrUndefined(masterContract) ||
          isNullOrUndefined(masterContract.salesDocumentId) ||
          isNullOrUndefined(product) ||
          isNullOrUndefined(product.salesDocument)
        ) {
          return true;
        }

        return product.salesDocument.salesDocumentId === masterContract.salesDocumentId;
      }),
    );
  },
);

export const getRequiredFieldsForSpecificationEntryGuard = createSelector(
  getLegalEntity,
  getBusinessLinePage,
  getLocationPage,
  (customer: ILegalEntity, businessLineState: IBusinessLineState, locationPageState: LocationPageState) => {
    return { customer, businessLineState, locationPageState };
  },
);

export const getFieldsForLoadingCatalogs = createSelector(
  getLegalEntity,
  getBusinessLine,
  getShippingCondition,
  (customer: ILegalEntity, businessLineState: BusinessLinesState, shippingContitionState: ShippingConditionsState) => {
    return { customer, businessLineState, shippingContitionState };
  },
);

export const getSpecificationProducts = createSelector(getSpecificationForm, getProducts);

export const getFormValues = createSelector(getSpecificationPage, (state) => state.specificationForm.forms);

export const getRmsSpecificationFormValues = createSelector(
  getRmsSpecificationPage,
  (state) => state.rmsSpecificationFormState.forms,
);

export const getRmsSpecificationQuantity = createSelector(getRmsSpecificationPage, (state) =>
  _safe(() => state.rmsSpecificationFormState.forms[0].values.quantity),
);

export const getRmsSpecificationUnloadType = createSelector(getRmsSpecificationPage, (state) =>
  _safe(() => state.rmsSpecificationFormState.forms[0].values.unloadType),
);

export const getRmsSpecificationPumpCapacity = createSelector(getRmsSpecificationPage, (state) =>
  _safe(() => state.rmsSpecificationFormState.forms[0].values.pumpCapacity),
);

export const getRmsSpecificationPumpLinearMeters = createSelector(getRmsSpecificationPage, (state) =>
  _safe(() => state.rmsSpecificationFormState.forms[0].values.linear_meters),
);

export const getCountryAndUnloadType = createSelector(
  getLegalEntity,
  getExperiencesState,
  getRmsSpecificationUnloadType,
  (legalEntity, experienceState, unloadType) => {
    return {
      countryCode: legalEntity.countryCode.trim(),
      experienceCode: experienceState.activeExperienceCode,
      unloadType,
    };
  },
);

export const getFormCounter = createSelector(getSpecificationPage, (state) => state.specificationForm.formsCounter);

/**
 * Returns the set-up for a product-specification form.
 * @type {MemoizedSelector<object, {formState: SpecificationFormState; anticipatedInvoiceState: IAnticipatedInvoiceState}>}
 */
export const getSpecificationFormWithInvoice = createSelector(
  getSpecificationForm,
  getAnticipatedInvoiceState,
  (formState, anticipatedInvoiceState) => {
    return { formState, anticipatedInvoiceState };
  },
);

export const getRMS_SpecificationState = createSelector(getOrderBuilderState, (_state) => {
  return _state.rmsSpecificationPage;
});

export const getOpenPlatformProductsState = createSelector(
  getRMS_SpecificationState,
  (state) => state && state.ProductsOpenPlatform,
);

export const getContractsBalancesOpenPlatformState = createSelector(
  getRMS_SpecificationState,
  (state) => state && state.ContractBalancesOpenPlatform,
);

export const getPaymentTermsOpenPlatformState = createSelector(
  getRMS_SpecificationState,
  (state) => state && state.PaymentTermsOpenPlatform,
);

export const getContractsBalancesOpenPlatform = createSelector(getContractsBalancesOpenPlatformState, (state) => {
  const balances: RmsContractBalance[] = [];
  for (const key in state.entities) {
    if (Object.prototype.hasOwnProperty.call(state.entities, key)) {
      balances.push(state.entities[key]);
    }
  }
  return balances;
});

export const getPaymentTermsOpenPlatform = createSelector(getPaymentTermsOpenPlatformState, (state) => {
  const paymentTerms: PaymentTerm[] = [];
  for (const key in state.entities) {
    if (Object.prototype.hasOwnProperty.call(state.entities, key)) {
      paymentTerms.push(state.entities[key]);
    }
  }
  return paymentTerms;
});

export const getOpenPlatformCombinationsState = createSelector(
  getRMS_SpecificationState,
  (state) => state && state.combinationsOpenPlatformState,
);

export const getOpenPlatformAddedValuesState = createSelector(
  getRMS_SpecificationState,
  (state) => state && state.AddedValuesOpenPlatform,
);

export const getOpenPlatformCombinations = createSelector(getOpenPlatformCombinationsState, (state) => {
  const combinations: CombinationOP[] = [];
  for (const key in state.entities) {
    if (Object.prototype.hasOwnProperty.call(state.entities, key)) {
      combinations.push(state.entities[key]);
    }
  }
  return combinations;
});

export const getOpenPlatformSelectedProduct = createSelector(
  getOpenPlatformProductsState,
  (state) => state.selectedProductId && state.entities[state.selectedProductId],
);

export const getOpenPlatformMandatoryAddedValues = createSelector(getOpenPlatformAddedValuesState, (state) => {
  const addedValues: ValueAddedOP[] = [];
  for (const key in state.mandatoryAddedValues.entities) {
    if (Object.prototype.hasOwnProperty.call(state.mandatoryAddedValues.entities, key)) {
      addedValues.push(state.mandatoryAddedValues.entities[key]);
    }
  }
  return addedValues;
});

export const getOpenPlatformFirstAddedValuesGroup = createSelector(getOpenPlatformAddedValuesState, (state) => {
  const addedValues: ValueAddedOP[] = [];
  for (const key in state.firstGroup.entities) {
    if (Object.prototype.hasOwnProperty.call(state.firstGroup.entities, key)) {
      addedValues.push(state.firstGroup.entities[key]);
    }
  }
  return addedValues;
});

export const getOpenPlatformSecondAddedValuesGroup = createSelector(getOpenPlatformAddedValuesState, (state) => {
  const addedValues: ValueAddedOP[] = [];
  for (const key in state.secondGroup.entities) {
    if (Object.prototype.hasOwnProperty.call(state.firstGroup.entities, key)) {
      addedValues.push(state.firstGroup.entities[key]);
    }
  }
  return addedValues;
});

export const getOpenPlatformThirdAddedValuesGroup = createSelector(getOpenPlatformAddedValuesState, (state) => {
  const addedValues: ValueAddedOP[] = [];
  for (const key in state.thirdGroup.entities) {
    if (Object.prototype.hasOwnProperty.call(state.firstGroup.entities, key)) {
      addedValues.push(state.firstGroup.entities[key]);
    }
  }
  return addedValues;
});

export const getOpenPlatformFourthAddedValuesGroup = createSelector(getOpenPlatformAddedValuesState, (state) => {
  const addedValues: ValueAddedOP[] = [];
  for (const key in state.fourthGroup.entities) {
    if (Object.prototype.hasOwnProperty.call(state.firstGroup.entities, key)) {
      addedValues.push(state.firstGroup.entities[key]);
    }
  }
  return addedValues;
});

export const getOpenPlatformFirstSelectedAddedValue = createSelector(
  getOpenPlatformAddedValuesState,
  (state) => state.firstGroup && state.firstGroup.entities[state.firstGroup.selectedAddedValueCode],
);

export const getOpenPlatformSecondSelectedAddedValue = createSelector(
  getOpenPlatformAddedValuesState,
  (state) => state.secondGroup && state.secondGroup.entities[state.secondGroup.selectedAddedValueCode],
);

export const getOpenPlatformThirdSelectedAddedValue = createSelector(
  getOpenPlatformAddedValuesState,
  (state) => state.thirdGroup && state.thirdGroup.entities[state.thirdGroup.selectedAddedValueCode],
);

export const getOpenPlatformFourthSelectedAddedValue = createSelector(
  getOpenPlatformAddedValuesState,
  (state) => state.fourthGroup && state.fourthGroup.entities[state.fourthGroup.selectedAddedValueCode],
);

//  -- Specification page

// Review page --
export const getReviewPage = createSelector(getOrderBuilderState, (state) => {
  return state.reviewPage;
});
// -- Review page

// Summary page --
export const getSummaryPage = createSelector(getOrderBuilderState, (state) => {
  return state.summaryPage;
});
// -- Summary page

// Custom --
export const getStateForLoadConfig = createSelector(
  getLegalEntity,
  getBusinessLinePage,
  getActiveExperienceCode,
  (customer: ILegalEntity, businessLineState: IBusinessLineState, experienceCode: string) => {
    return { customer, businessLineState, experienceCode };
  },
);

export const getStateForLocationPageConfig = createSelector(
  getLegalEntity,
  getBusinessLinePage,
  getLocationPage,
  (customer: ILegalEntity, businessLineState: IBusinessLineState, locationState: LocationPageState) => {
    return { customer, businessLineState, locationState };
  },
);

export const getJobSitesLoaded = createSelector(getJobsiteState, (state) => {
  return state.loaded;
});

export const getJobSitesLoading = createSelector(getJobsiteState, (state) => {
  return state.loading;
});

export const getPumpMessageDisplay = createSelector(
  getSpecificationForm,
  (state: SpecificationFormState) => state.displayPumpingInformationDialog,
);
export const getPumpingAvailability = createSelector(
  getSpecificationForm,
  (state: SpecificationFormState) => state.ignorePumpingServiceFromSelection,
);
// -- Custom

export const getFormState = createSelector(
  getLocationSpecificationState,
  (state) => state && state.locationSpecificationFormState,
);

export const getContractsState = createSelector(getLocationSpecificationState, (state) => state && state.contracts);
export const getCUnitsState = createSelector(getLocationSpecificationState, (state) => state && state.units);
export const getPlantsState = createSelector(getLocationSpecificationState, (state) => state && state.plants);
export const getAdditionalServicesState = createSelector(
  getLocationSpecificationState,
  (state) => state && state.additionalServices,
);

export const getContractsArray = createSelector(getContractsState, fromContracts.getContracts);
export const getUnitsArray = createSelector(getCUnitsState, fromUnits.getUnits);
export const getPlantsArray = createSelector(getPlantsState, fromPlants.getPlants);
export const getAdditionalServiceArray = createSelector(
  getAdditionalServicesState,
  fromAdditionalServices.getAdditionalServices,
);

export const getOrderDetailsForms = createSelector(getFormState, (state) => state && state.forms);

export const getLocationJobsiteState = createSelector(getLocationSpecificationState, (state) => state && state.jobsite);

export const getPODState = createSelector(getLocationSpecificationState, (state) => state && state.pointOfDelivery);

export const getContactPersonState = createSelector(
  getLocationSpecificationState,
  (state) => state && state.contactPerson,
);

const getReceivingPersonsState = createSelector(
  getLocationSpecificationState,
  (state) => state && state.receivingPersons,
);

const getContactPersonRoles = createSelector(
  getLocationSpecificationState,
  (state) => state && state.contactPersonRoles,
);

export const getLocationJobsites = createSelector(getLocationJobsiteState, (state) => {
  return state.jobsites;
});

export const getJobsiteBlock = createSelector(getLocationJobsiteState, (state) => {
  return state.hasBlockedJobsites;
});

export const getJobsiteModal = createSelector(getLocationJobsiteState, (state) => {
  return state.openModalJobsite;
});

export const getSelectedLocationJobsite = createSelector(getLocationJobsiteState, (state) => {
  return state.selected;
});

export const getSelectedExperience = createSelector(getLocationJobsiteState, (state) => {
  return { experience: state.experience, selectedJobsite: state.selected };
});

export const getPreviouslySelectedLocationJobsite = createSelector(getLocationJobsiteState, (state) => {
  return state.previouslySelected;
});

export const getContactPersonsArray = createSelector(getReceivingPersonsState, fromReceivingPersons.getContactPersons);

export const getSelectedReceivingPersonId = createSelector(
  getReceivingPersonsState,
  fromReceivingPersons.getSelectedContactPersonId,
);

export const getContactPersonRolesArray = createSelector(
  getContactPersonRoles,
  fromContactPersonRoles.getContactPersonRoles,
);

export const getSelectedLocationJobsiteAddress = createSelector(getLocationJobsiteState, (state) => {
  return state.selectedJobsiteAddress;
});

export const getSelectedReceivingPerson = createSelector(
  getReceivingPersonsState,
  getContactPersonsArray,
  (state, contactPersons) => {
    const receivingPerson = contactPersons.find((x) => x.contactId === state.selectedContactPersonId);
    return receivingPerson;
  },
);

// FEATURE TOGGLE

export const getShowContractInputToggleFlag = createSelector(getFeatureToggleState, (state) => {
  return state.enableContractDetail;
});

/**
 * Main salector of Admixtures Specification
 */
export const getAdtMainSelector = createSelector(getOrderBuilderState, (state) => {
  return state.adtSpecification;
});

export const getPodAddressName = createSelector(getPointOfDeliveryState, (state) => {
  return state && state.addressNames;
});

export const getPodAndAddresName = createSelector(
  getPointOfDeliveries,
  getPodAddressName,
  (pointOfdelivery, addresNames) => {
    return {
      pointOfdelivery,
      addresNames,
    };
  },
);
